import { defineComponent, PropType, computed } from "vue";
import $ from "jquery";
import turndownService from "turndown";
import { Container } from "aurelia-dependency-injection";
import { mapState } from "pinia";

import { IMessageItem } from "../../../vue-interfaces/messaging-service/i-message";
import { IToolbarCustomButtons } from "../../../vue-interfaces/messaging-service/i-summer-editor";
import { markdownToHtml } from "../../../../common/vue-helpers/modifiers/markdown-to-html-modifier";
import { sanitizeMarkup } from "./../../../../common/vue-helpers/modifiers/value-modifier";
import { MessageService } from "../../../../services/message-service";
import { PlaceholdersListEnum } from "../../../../enums/enums";
import { sortPlaceholdersList } from "../../../../common/vue-helpers/modifiers/format-message";

import SummerEditor from "../SummerEditor/SummerEditor.vue";
import SelectProfile from "../../../vue-dialogs/SelectProfile/SelectProfile.vue";
import { useUserStore } from "../../../../pages-vue/MessageCenter/store/useUsersStore";
import { useConversationStore } from "../../../../pages-vue/MessageCenter/store/useConversationStore";

export default defineComponent({
    components: {
        SummerEditor
    },
    props: {
        isImportant: { type: Boolean, default: false },
        toggleImportant: { type: Function, default: null },
        send: { type: Function, default: null },
        initialMessage: { type: String, default: "" },
        shouldDisableSend: { type: Boolean, default: false },
        refsName: { type: String, default: "chatEditorRef" },
        message: { type: Object as PropType<IMessageItem>, default: null },
        closeEditMsg: { type: Function, default: null },
        isFamilyPortal: { type: Boolean, default: false }
    },
    data() {
        return {
            result: null as string,
            editorOptions: {},
            customButtons: {} as IToolbarCustomButtons,
            _turndownService: new turndownService(),
            dialogRef: null,
            _messageService: null as MessageService,
            isShowSelectProfile: false,
            selectedProfile: [],
            allSelectedProfiles: [],
            taggedUsersList: [] as any[],
            msgFiles: [] as any,
            editMsg: null as any,
            markdownToHtml,
            sanitizeMarkup
        };
    },

    created() {
        this._messageService = Container.instance.get(MessageService);
        if (this.message) {
            this.updateEditMsgDetails();
        }
        if (this.activeConversation) {
            if (this.message) {
                this.customButtons = {
                    important: this.markAsImportantButton,
                    shareProfile: this.shareProfile,
                    discardBtn: this.discardEditBtn
                };
            } else {
                this.customButtons = {
                    important: this.markAsImportantButton,
                    shareProfile: this.shareProfile
                };
            }
        } else {
            if (this.isFamilyPortal) {
                this.customButtons = null;
            } else {
                this.customButtons = {
                    important: this.markAsImportantButton
                };
            }
        }
        this.editorOptions = {
            buttons: this.customButtons
        };
    },

    provide() {
        return {
            dialogRef: computed(() => this.dialogRef)
        };
    },

    computed: {
        ...mapState(useConversationStore, {
            activeConversation: "getActiveConversation"
        }),
        ...mapState(useUserStore, ["getMultipleUsers"])
    },

    methods: {
        markAsImportantButton() {
            let ui = ($ as any).summernote.ui;
            // create button
            let button = ui.button({
                contents: `<i class="important-msg" />`,
                tooltip: this.isImportant ? "Mark as Standard" : "Mark as Important ",
                className: "mark-as-imp-btn",
                click: () => {
                    const tooltipText = this.isImportant ? "Mark as Important" : "Mark as Standard ";
                    const btn = $(".note-btn.mark-as-imp-btn");
                    btn.attr("data-original-title", tooltipText);
                    btn.attr("aria-label", tooltipText);
                    this.toggleImportant(!this.isImportant);
                }
            });

            return button.render(); // return button as jquery object
        },

        shareProfile() {
            const ui = ($ as any).summernote.ui;
            const self = this;

            const button = ui.button({
                tooltip: "Share Profile",
                contents: `
                        <div
                            class="dropdown-toggle"
                            data-bs-toggle="dropdown"
                        >
                            <i class="share-user"></i>
                        </div>

                        <div class="dropdown-menu dropdown-variable p-0">
                            <a class="dropdown-item rounded-top"
                            href="#"
                            data-type="employee"
                            aria-label="Employee Profile">
                                <div class="d-flex justify-content-between">
                                    <div class="pe-4">
                                        <p class="mb-0 small fw-semibold">Employee Profile</p>
                                        <p class="mb-0 small text-muted">Share Contact</p>
                                    </div>
                                    <img src="${require("../../../../assets/images/chat/share-profile.svg")}"/>
                                </div>
                            </a>

                            <a class="dropdown-item rounded-bottom"
                            href="#"
                            data-type="patient"
                            aria-label="Patient Profile">
                                <div class="d-flex justify-content-between">
                                    <div class="pe-4">
                                        <p class="mb-0 small fw-semibold">Patient Profile</p>
                                        <p class="mb-0 small text-muted">Share Contact</p>
                                    </div>
                                    <img src="${require("../../../../assets/images/chat/share-profile.svg")}"/>
                                </div>
                            </a>
                        </div>
             `
            });

            const $btn = $(button.render());

            // Handle dropdown item click
            $btn.on("click", ".dropdown-item", function (e) {
                e.preventDefault();
                const type = $(this).data("type");
                type === "employee" ? self.employeeList() : self.patientList();
            });

            return $btn;
        },

        discardEditBtn() {
            let ui = ($ as any).summernote.ui;
            let button = ui.button({
                contents: `<i class="fas fa-times"/>`,
                tooltip: `Discard`,
                click: () => {
                    this.closeEditMsg();
                }
            });
            return button.render();
        },
        updateResult(newResult: string) {
            this.result = newResult;
        },
        updateSelectedProfile(newProfiles: any, allUpdatedProfiles: any) {
            this.selectedProfile = newProfiles;
            this.allSelectedProfiles = allUpdatedProfiles;
        },
        updateMsgFiles(newFiles: any) {
            this.msgFiles = newFiles;
        },
        async sendMessage(files: any) {
            let params: {
                message?: string;
                files?: any;
                placeHoldersList?: any;
            } = { message: "", files: [], placeHoldersList: [] };
            if (this.allSelectedProfiles.length > 0) {
                const profiles = this.allSelectedProfiles.map((profile: any) => {
                    if (profile?.name) {
                        // when user is editing a message
                        return {
                            type: profile.type,
                            id: profile.id,
                            agencyId: profile.agencyId,
                            applicationId: profile.application,
                            agencyName: profile.agencyName,
                            name: profile.name
                        };
                    } else {
                        const isEmployeeProfile = profile.hasOwnProperty("id");
                        return {
                            type: isEmployeeProfile
                                ? PlaceholdersListEnum.ShareUser
                                : PlaceholdersListEnum.SharePatient,
                            id: isEmployeeProfile ? profile.sourceUserId : profile.patientId,
                            agencyId: profile.agencyId,
                            applicationId: profile.application,
                            agencyName: profile.agencyName,
                            name: profile.firstName + " " + profile.lastName
                        };
                    }
                });
                params.placeHoldersList = profiles;
                this.selectedProfile = [];
                this.allSelectedProfiles = [];
            }

            if (!!this.result && this.result.length > 0) {
                const res = this.replaceTaggedUsers(this.result);
                let htmlMarkdown = this.getMarkdownFromHtml(res);
                let message = htmlMarkdown.replace(/%{TAGGEDUSER}%/g, "%{TAGGED_USER}% ");
                if (!!message && message.length > 0) {
                    params.message = message;
                }
                (this.$refs.summerEditorRef as typeof SummerEditor).reset();
            }
            if (
                Object.keys(params).length > 0 ||
                files.length > 0 ||
                params.placeHoldersList?.length > 0 ||
                this.taggedUsersList.length > 0
            ) {
                params.files = files;
                params.placeHoldersList = [...params.placeHoldersList, ...this.taggedUsersList];
                await this.send(params);
                this.taggedUsersList = [];
                this.msgFiles = [];
            }
        },
        getMarkdownFromHtml(html: string) {
            return this._turndownService.turndown(html);
        },
        replaceTaggedUsers(msg: string) {
            const parser = new DOMParser();
            const html = parser.parseFromString(msg, "text/html").body;
            let elements = html.querySelectorAll('[data-actiontype="tagged-user"]');
            if (elements.length > 0) {
                elements.forEach((ele) => {
                    const users = this.getMultipleUsers(this.activeConversation.loginIds);
                    const details = users.find((user) => user.loginId === ele.id);
                    this.taggedUsersList.push({
                        type: PlaceholdersListEnum.TaggedUser,
                        id: details.loginId,
                        name: details.firstName + " " + details.lastName,
                        applicationId: 0, // optional field for tagged user, hence sending 0
                        agencyId: "122ec20a-705b-4974-9437-8f6233a6e953", // optional field, sending random agencyID till the backend is fixed.
                        agencyName: ""
                    });
                    ele.replaceWith(`%{TAGGEDUSER}%`);
                });
                return html.outerHTML;
            }
            return msg;
        },
        employeeList() {
            this.isShowSelectProfile = false;
            this.dialogRef = this.$dialog.open(SelectProfile, {
                props: {
                    modal: true,
                    showHeader: false,
                    style: {
                        width: "400px",
                        height: "500px"
                    }
                },
                data: {
                    name: "employee",
                    conversationId: this.activeConversation.id
                },
                onClose: (response: any) => {
                    if (response.data !== "cancel") {
                        this.selectedProfile = response.data;
                        this.allSelectedProfiles.push(...response.data);
                    }
                }
            });
        },
        patientList() {
            this.isShowSelectProfile = false;
            this.dialogRef = this.$dialog.open(SelectProfile, {
                props: {
                    modal: true,
                    showHeader: false,
                    style: {
                        width: "400px",
                        height: "505px"
                    }
                },

                data: {
                    name: "patient",
                    conversationId: this.activeConversation.id
                },
                onClose: (response: any) => {
                    if (response.data !== "cancel") {
                        this.selectedProfile = response.data;
                        this.allSelectedProfiles.push(...response.data);
                    }
                }
            });
        },
        toggleSelectProfile() {
            this.isShowSelectProfile = !this.isShowSelectProfile;
        },

        async updateEditMsgDetails() {
            const { sharedProfiles } = sortPlaceholdersList(this.message?.placeHoldersList);
            this.allSelectedProfiles = sharedProfiles;
            this.selectedProfile = sharedProfiles;
            this.result = sanitizeMarkup(markdownToHtml(this.message.text));
            this.msgFiles = this.message?.files ?? [];
            this.editMsg = { files: this.msgFiles, result: this.result };
        }
    }
});
